import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { isString } from 'lodash';

import {
   SelfPickupGreenIcon,
   SelfPickupIcon,
   TruckDeliveryGreenIcon,
   TruckDeliveryIcon,
} from 'assets';
import { SectionContainer, StyledSelect } from 'components/shared';
import { AddressData, Option, SelectValue, StorageGroupOption, StorageOption } from 'interfaces';
import { Banner, Card } from 'storybook';

import styles from './EditDeliverySection.module.css';

type EditDeliverySectionProps = {
   selectedAddress: AddressData | 'SELF_PICKUP' | null;
   availableAddresses: AddressData[];
   selectedStorage?: Option;
   availableStorages?: StorageGroupOption[];
   onChangeAddress: (
      address: SelectValue | StorageOption | 'SELF_PICKUP' | null,
      field: string,
   ) => void;
   disabledSelfPickup?: boolean;
   touched?: boolean;
   isMobile?: boolean;
   onSave?: () => void;
   onCancel?: () => void;
   isLoading?: boolean;
   error?: string;
};

const EditDeliverySection = ({
   selectedAddress,
   availableAddresses,
   selectedStorage,
   availableStorages,
   onChangeAddress,
   disabledSelfPickup,
   error,
   touched,
   isMobile,
   onSave,
   onCancel,
   isLoading,
}: EditDeliverySectionProps) => {
   const { t } = useTranslation();

   const handleDeliveryAddressChange = (value: SelectValue) => {
      if (selectedStorage && selectedAddress === 'SELF_PICKUP') {
         onChangeAddress(null, 'storage');
      }
      onChangeAddress(value, 'address');
   };

   return (
      <SectionContainer
         title={t('delivery.title')}
         onSave={onSave}
         onCancel={onCancel}
         isLoading={isLoading}
      >
         {error && touched && (
            <Banner fullWidth variant="error" children={error} style={{ marginBottom: 24 }} />
         )}
         <div className={styles.cardsContainer}>
            <Card
               title={t('delivery.deliveryTitle')}
               displayCheckedIcon
               checked={!error && !!selectedAddress && selectedAddress !== 'SELF_PICKUP'}
               icon={
                  !error && !!selectedAddress && selectedAddress !== 'SELF_PICKUP' ? (
                     <TruckDeliveryGreenIcon />
                  ) : (
                     <TruckDeliveryIcon />
                  )
               }
               bottomContent={
                  <div className={styles.cardContent}>
                     <StyledSelect
                        placeholder={t('delivery.selectAddress')}
                        value={
                           isString(selectedAddress) || selectedAddress === null
                              ? undefined
                              : selectedAddress
                        }
                        onChange={handleDeliveryAddressChange}
                        options={availableAddresses}
                        disabled={disabledSelfPickup}
                        mobileView={isMobile}
                        classNames={{ select: styles.addressSelect }}
                     />
                  </div>
               }
               disabled={disabledSelfPickup}
               className={classNames(styles.cardLarge, {
                  [styles.cardDisabled]: disabledSelfPickup,
               })}
            />
            <Card
               displayCheckedIcon
               {...(!availableStorages
                  ? { onClick: () => onChangeAddress('SELF_PICKUP', 'address') }
                  : {})}
               icon={
                  selectedAddress === 'SELF_PICKUP' ? <SelfPickupGreenIcon /> : <SelfPickupIcon />
               }
               checked={selectedAddress === 'SELF_PICKUP'}
               title={t('delivery.selfPickupTitle')}
               bottomContent={
                  !availableStorages ? (
                     <p className={styles.cardText}>{t('delivery.selfPickupInfo')}</p>
                  ) : (
                     <>
                        <p className={styles.cardText}>{t('delivery.selectPickupPlace')}</p>
                        <StyledSelect
                           value={
                              selectedStorage && selectedAddress === 'SELF_PICKUP'
                                 ? selectedStorage
                                 : undefined
                           }
                           onChange={(value: StorageOption) => {
                              onChangeAddress(value, 'storage');
                              onChangeAddress('SELF_PICKUP', 'address');
                           }}
                           groups={availableStorages}
                           mobileView={isMobile}
                           classNames={{ select: styles.addressSelect }}
                        />
                     </>
                  )
               }
               className={styles.cardLarge}
            />
         </div>
      </SectionContainer>
   );
};

export default EditDeliverySection;
